import React, { useEffect, useRef, useState } from 'react';

interface LightReplyModalProps {
  isOpened: boolean;
  onClose: () => void;
  onSubmit: (data: { text: string }) => void;
  onOpenAttachments?: () => void;
  attachmentCount?: number;
  attachmentsDisabled?: boolean;
}

/** Light-native text-reply bottom sheet. Replaces the legacy dark AnswerForm
 *  shell when running inside TrainScreenLight — matches the paper/sage
 *  typography and respects iOS safe-area-inset-bottom so the submit button
 *  never lands under the home-indicator. */
function LightReplyModal({
  isOpened,
  onClose,
  onSubmit,
  onOpenAttachments,
  attachmentCount = 0,
  attachmentsDisabled = false,
}: LightReplyModalProps) {
  const [reply, setReply] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isOpened) {
      const t = setTimeout(() => textareaRef.current?.focus(), 250);
      return () => clearTimeout(t);
    }
    setReply('');
  }, [isOpened]);

  const submit = () => {
    const trimmed = reply.trim();
    if (trimmed.length < 2) return;
    onSubmit({ text: trimmed });
    setReply('');
    onClose();
  };

  return (
    <>
      <div
        aria-hidden
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          backgroundColor: 'rgba(238, 239, 232, 0.55)',
          backdropFilter: 'blur(14px) saturate(1.1)',
          WebkitBackdropFilter: 'blur(14px) saturate(1.1)',
          opacity: isOpened ? 1 : 0,
          pointerEvents: isOpened ? 'auto' : 'none',
          transition: 'opacity 280ms ease',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          left: '50%',
          bottom: 0,
          zIndex: 9999,
          width: 'min(600px, 100vw)',
          transform: `translateX(-50%) translateY(${isOpened ? '0%' : '110%'})`,
          transition: 'transform 320ms cubic-bezier(.22,.61,.36,1)',
          willChange: 'transform',
        }}
      >
        <div
          className="lp-card"
          style={{
            padding: 'clamp(20px, 4vw, 28px)',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + clamp(20px, 4vw, 28px))',
            borderRadius: '22px 2px 22px 2px',
            background: 'var(--lp-paper-2)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              marginBottom: 14,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              style={{ color: 'var(--lp-ink-soft)' }}
            >
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
            </svg>
            <span className="lp-eyebrow-text" style={{ color: 'var(--lp-ink-soft)' }}>
              Respond below
            </span>
          </div>
          <div
            style={{
              height: 1,
              width: '100%',
              background: 'rgba(40, 50, 40, 0.10)',
              marginBottom: 16,
            }}
          />
          <div style={{ position: 'relative' }}>
            <textarea
              ref={textareaRef}
              className="lp-input lp-textarea"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Type your response here…"
              rows={6}
              style={{
                width: '100%',
                minHeight: 180,
                fontFamily: 'var(--lp-serif)',
                fontSize: 17,
                lineHeight: 1.45,
                // Reserve room in the top-right so typed text never slides
                // under the floating paperclip button.
                paddingRight: onOpenAttachments ? 56 : undefined,
              }}
            />
            {onOpenAttachments && (
              <button
                type="button"
                onClick={onOpenAttachments}
                disabled={attachmentsDisabled}
                aria-label={
                  attachmentCount > 0
                    ? `Attach (${attachmentCount} attached)`
                    : 'Attach'
                }
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  width: 40,
                  height: 40,
                  padding: 0,
                  border: 0,
                  borderRadius: '50%',
                  // Solid gradient fill reads instantly as a button; the
                  // white paperclip glyph + soft drop shadow lift it off the
                  // paper textarea.
                  background:
                    'linear-gradient(135deg, var(--lp-violet) 0%, var(--lp-pink) 45%, var(--lp-orange) 90%)',
                  display: 'grid',
                  placeItems: 'center',
                  cursor: attachmentsDisabled ? 'not-allowed' : 'pointer',
                  opacity: attachmentsDisabled ? 0.45 : 1,
                  boxShadow:
                    '0 1px 0 rgba(255,255,255,0.45) inset, ' +
                    '0 6px 16px -6px rgba(236, 72, 153, 0.55), ' +
                    '0 2px 6px -2px rgba(40,50,40,0.18)',
                  transition: 'transform 180ms ease, box-shadow 180ms ease',
                }}
                onMouseDown={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform =
                    'scale(0.94)';
                }}
                onMouseUp={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform =
                    'scale(1)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform =
                    'scale(1)';
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  {/* Lucide Paperclip — a more recognisable clip silhouette
                      than the previous single-path version. */}
                  <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                {attachmentCount > 0 && (
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      minWidth: 18,
                      height: 18,
                      padding: '0 5px',
                      borderRadius: 999,
                      background: 'var(--lp-paper-warm)',
                      color: 'var(--lp-ink)',
                      fontFamily: 'var(--lp-mono)',
                      fontSize: 10,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow:
                        '0 0 0 2px var(--lp-paper-2), 0 2px 6px -2px rgba(40,50,40,0.35)',
                    }}
                  >
                    {attachmentCount}
                  </span>
                )}
              </button>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 18,
            }}
          >
            <button
              type="button"
              onClick={submit}
              disabled={reply.trim().length < 2}
              className="lp-btn lp-btn-primary"
              style={{
                minWidth: 180,
                opacity: reply.trim().length < 2 ? 0.5 : 1,
                cursor: reply.trim().length < 2 ? 'not-allowed' : 'pointer',
              }}
            >
              <span style={{ letterSpacing: '0.18em' }}>SUBMIT</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default LightReplyModal;
